import type { Lesson, Term } from '../types';

type TermWindow = Pick<Term, 'isActive' | 'startDate' | 'endDate'>;

/**
 * Lesson owner flows: editing can affect enrolments mid-schedule — confirm first.
 *
 * – **Group** + `termId`: warn when `term.isActive` and `now` is within `[startDate, endDate]`.
 * – **Camp**: warn when `now` is within `[startDate, endDate]` (both required).
 *
 * Always returns **false** for private lessons and when term context is missing for a group lesson.
 */
export function shouldWarnWhenEditingLessonInRunningSchedule(
  lesson: Pick<Lesson, 'lessonType' | 'termId' | 'startDate' | 'endDate'>,
  termForGroupLesson: TermWindow | null | undefined,
  now: Date = new Date()
): boolean {
  if (lesson.lessonType === 'group' && lesson.termId) {
    if (!termForGroupLesson?.isActive) return false;
    const start = new Date(termForGroupLesson.startDate);
    const end = new Date(termForGroupLesson.endDate);
    return now >= start && now <= end;
  }

  if (lesson.lessonType === 'camp') {
    if (!lesson.startDate || !lesson.endDate) return false;
    const start = new Date(lesson.startDate as Date | string);
    const end = new Date(lesson.endDate as Date | string);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
    return now >= start && now <= end;
  }

  return false;
}
